import { useCallback, useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateRange } from 'react-day-picker';
import { useForm, useWatch } from 'react-hook-form';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { useSimpleModal } from '@/features/_shared/hooks/use-handle-local-modal';
import { axiosExtractErrorMessage, AxiosErrorCustom } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { useDebounce } from '@/hooks/use-debounce';
import { useListTicketMemberQuery } from '../../../api/get-ticket-member.api';
import { useListLabelTicketQuery } from '@/features/ticketings/label-ticketing/api/get-list-label-ticket.api';
import { useListCaseQuery } from '@/features/cases/case/api/get-list-case.api';
import { useSingleTicketQuery } from '../../../api/get-single-ticket.api';
import { GET_TICKET_ACTIVITY_QUERY_KEY } from '@/features/ticketings/tickets/ticket-activity/api/get-list-ticket-activity.api';
import {
  CreateTicketValidationSchema,
  TCreateTicketValidation,
  useCreateTicketMutation,
} from '../../../api/create-ticket.api';
import { IDuplicateTicketModalProps } from './duplicate-ticket-modal';
import { useListTicketBySpaceQuery } from '../../../api/get-ticket-by-space.api';
import { sortTicketsByPosition, getNewPositionBetween, TicketWithPosition } from '../../../utils/ordering-ticket.utils';
import { useHasTriggered } from '@/hooks/use-has-triggered';
import { useFileAttachment, FormAttachment } from '../use-file-attachment';

const useDuplicateTicket = ({ spaceId, ticketId }: IDuplicateTicketModalProps) => {
  // ===== Hooks =====
  const showToast = useCustomToast();
  const ticketingModalActions = useTicketingModalRootActions();
  const [searchCase, setSearchCase] = useState('');

  // ===== Form =====
  const form = useForm<TCreateTicketValidation>({
    resolver: zodResolver(CreateTicketValidationSchema),
    mode: 'onChange',
    defaultValues: {
      spaceId: '',
      spaceColumnId: '',
      position: '',
      title: '',
      startDate: undefined,
      endDate: undefined,
      content: undefined,
      labelTicketIds: [],
      userIds: [],
      caseIds: [],
      attachments: [],
    },
  });

  // Watch form values to avoid multiple form.watch() calls
  const watchedAttachments = useWatch({ control: form.control, name: 'attachments' }) as FormAttachment[] | undefined;

  // ===== Init Guard =====
  const {
    hasTriggered: hasTriggeredFormInitializedGuard,
    reset: resetFormInitialized,
    markAsTriggered: markFormAsInitialized,
  } = useHasTriggered();

  // ===== Date Modal =====
  const {
    values: dateValues,
    isOpen: isOpenDateModal,
    handleSetValuesModal: handleSetDateValuesModal,
    handleOpenModal: handleOpenDateModal,
    handleCloseModal: handleCloseDateModal,
  } = useSimpleModal<DateRange | null>({
    from: undefined,
    to: undefined,
  });

  // ===== File Attachment Hook =====
  const attachments = useFileAttachment({
    initialAttachments: watchedAttachments || [],
    onAttachmentsChange: (newAttachments) => {
      form.setValue('attachments', newAttachments);
    },
    onError: (error) => {
      form.setError('attachments', {
        type: 'manual',
        message: error,
      });
    },
  });

  // ===== Debounced Search =====
  const debouncedSearchCase = useDebounce(searchCase, 300);
  const handleSearchCase = useCallback((value: string) => setSearchCase(value), []);

  // ===== Data Queries =====
  // Single Ticket
  const { data: singleTicketQuery, isLoading: isLoadingSingleTicketQuery } = useSingleTicketQuery({
    params: { ticketId },
    queryConfig: { enabled: !!ticketId },
  });
  const ticketData = useMemo(() => {
    if (!singleTicketQuery?.payload || isLoadingSingleTicketQuery) return null;
    return singleTicketQuery.payload;
  }, [singleTicketQuery, isLoadingSingleTicketQuery]);

  // List Tickets By Space (to get tickets in the same column for position calculation)
  const { data: listTicketBySpaceQuery } = useListTicketBySpaceQuery({
    params: { spaceId },
    queryConfig: { enabled: !!spaceId && !!ticketData },
  });

  // Ticket Members
  const { data: listTicketMemberData, isLoading: isLoadingListTicketMemberData } = useListTicketMemberQuery({
    params: { page: 1, limit: 500, spaceId, search: '' },
    queryConfig: { enabled: !!spaceId },
  });
  const listTicketMemberOptions = useMemo(() => listTicketMemberData?.payload?.rows || [], [listTicketMemberData]);

  // Cases
  const { data: listCaseData, isLoading: isLoadingListCaseData } = useListCaseQuery({
    params: { page: 1, limit: 500, search: debouncedSearchCase },
    queryConfig: { enabled: !!spaceId },
  });
  const listCaseOptions = useMemo(() => listCaseData?.payload?.rows || [], [listCaseData]);

  // Label Tickets
  const { data: listLabelTicketData, isLoading: isLoadingListLabelTicketData } = useListLabelTicketQuery({
    params: { page: 1, limit: 500 },
    queryConfig: { enabled: !!spaceId },
  });
  const listLabelTicketOptions = useMemo(() => listLabelTicketData?.payload?.rows || [], [listLabelTicketData]);

  // ===== Get Space Column ID =====
  const spaceColumnId = useMemo(() => {
    if (!listTicketBySpaceQuery?.payload || !ticketId) {
      return undefined;
    }

    // Find the column that contains the reference ticket
    const column = listTicketBySpaceQuery.payload.find((col) => col.tickets.some((t) => t.ticketId === ticketId));
    return column?.spaceColumnId;
  }, [listTicketBySpaceQuery, ticketId]);

  // ===== Calculate Position =====
  const calculatedPosition = useMemo(() => {
    if (!spaceColumnId || !ticketData?.position || !listTicketBySpaceQuery?.payload) {
      return undefined;
    }

    // Find the column that contains the reference ticket
    const column = listTicketBySpaceQuery.payload.find((col) => col.spaceColumnId === spaceColumnId);
    if (!column || !column.tickets || column.tickets.length === 0) {
      return undefined;
    }

    // Sort tickets by position
    const sortedTickets = sortTicketsByPosition(column.tickets as TicketWithPosition[]);

    // Find the index of the reference ticket
    const referenceTicketIndex = sortedTickets.findIndex((t) => t.ticketId === ticketId);
    if (referenceTicketIndex === -1) {
      return undefined;
    }

    // Get the reference ticket and the next ticket (if any)
    const referenceTicket = sortedTickets[referenceTicketIndex];
    const nextTicket = sortedTickets[referenceTicketIndex + 1] ?? null;

    // Validate that reference ticket has a valid position
    if (!referenceTicket.position) {
      // If reference ticket has no position, we can't calculate a position after it
      // This shouldn't happen, but handle it gracefully
      return undefined;
    }

    // Validate positions before generating new position
    // Check if next ticket's position is valid and greater than reference ticket's position
    const isValidNextPosition = !nextTicket || !nextTicket.position || referenceTicket.position < nextTicket.position;

    // If positions are invalid (next position <= reference position), use fallback
    if (!isValidNextPosition) {
      // Fallback: generate position after reference ticket only (ignore next ticket)
      return getNewPositionBetween(referenceTicket, null);
    }

    // Try to generate position between reference and next ticket
    try {
      const newPosition = getNewPositionBetween(referenceTicket, nextTicket);
      return newPosition;
    } catch (error) {
      // If generation fails (e.g., positions are invalid), fallback to generating after reference ticket
      // eslint-disable-next-line no-console
      console.warn('Failed to generate position between tickets, using fallback', {
        referenceTicket: referenceTicket.position,
        nextTicket: nextTicket?.position,
        error,
      });
      // Fallback: generate position after reference ticket only
      return getNewPositionBetween(referenceTicket, null);
    }
  }, [spaceColumnId, ticketData, listTicketBySpaceQuery, ticketId]);

  // ===== Mutations =====
  // Create Ticket (duplicate)
  const mutationCreateTicket = useCreateTicketMutation({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Error duplicate ticket, please try again';
        const displayMessage = Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage;
        showToast({ message: displayMessage, type: 'error' });
      },
      onSuccess: async () => {
        showToast({ message: 'Ticket duplicated successfully!', type: 'success' });
        ticketingModalActions.closeModal('DuplicateTicketModal');
        form.reset();
        attachments.clearFile();

        // Invalidate queries
        if (spaceId) {
          await Promise.all([
            // queryClient.invalidateQueries({
            //   queryKey: [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }],
            // }),
            // queryClient.invalidateQueries({
            //   queryKey: [GET_SINGLE_TICKET_QUERY_KEY, { ticketId }],
            // }),
            queryClient.invalidateQueries({
              queryKey: [GET_TICKET_ACTIVITY_QUERY_KEY, { ticketId }],
            }),
          ]);
        }
      },
    },
  });

  // ===== Modal State =====
  const isDuplicateTicketOpen = useMemo(() => {
    return ticketingModalActions.checkIsOpenModal('DuplicateTicketModal');
  }, [ticketingModalActions]);

  // ===== Effects =====
  // Reset init guard when ticketId changes
  useEffect(() => {
    resetFormInitialized();
  }, [ticketId, resetFormInitialized]);

  // Initialize form with ticket data
  useEffect(() => {
    if (
      ticketData &&
      !isLoadingSingleTicketQuery &&
      !hasTriggeredFormInitializedGuard() &&
      calculatedPosition &&
      spaceColumnId
    ) {
      form.reset({
        spaceId,
        spaceColumnId: spaceColumnId,
        position: calculatedPosition,
        title: ticketData?.title || '',
        startDate: ticketData?.startDate || undefined,
        endDate: ticketData?.endDate || undefined,
        content: ticketData?.content || undefined,
        labelTicketIds: [...(ticketData?.ticketLabels?.map((l) => l.labelTicketId) || [])],
        userIds: [...(ticketData?.users?.map((u) => u.userId) || [])],
        caseIds: [...(ticketData?.cases?.map((c) => c.caseId) || [])],
        attachments: [...(ticketData?.attachments || [])],
      });

      if (ticketData?.startDate && ticketData?.endDate) {
        handleSetDateValuesModal({
          from: new Date(ticketData?.startDate),
          to: new Date(ticketData?.endDate),
        });
      }

      markFormAsInitialized();
    }
  }, [
    ticketData,
    isLoadingSingleTicketQuery,
    spaceId,
    calculatedPosition,
    spaceColumnId,
    handleSetDateValuesModal,
    form,
    hasTriggeredFormInitializedGuard,
    markFormAsInitialized,
  ]);

  // ===== Handlers =====

  // Close Modal
  const handleCloseModal = useCallback(() => {
    ticketingModalActions.closeModal('DuplicateTicketModal');
  }, [ticketingModalActions]);

  // Submit Form
  const handleSubmitForm = useCallback(
    (data: TCreateTicketValidation) => {
      // Ensure position is set to the calculated position
      const dataToCreate = {
        ...data,
        position: calculatedPosition || data.position,
      };
      mutationCreateTicket.mutate(dataToCreate);
    },
    [mutationCreateTicket, calculatedPosition]
  );

  // ===== Return =====
  return {
    // Modal state
    isDuplicateTicketOpen,
    handleCloseModal,

    // Form
    form,
    handleSubmitForm,
    isLoadingCreateTicket: Boolean(mutationCreateTicket?.isPending),

    // Date modal
    dateValues,
    isOpenDateModal,
    handleOpenDateModal,
    handleCloseDateModal,
    handleSetDateValuesModal,

    // Ticket members
    listTicketMemberOptions,
    isLoadingListTicketMemberData,

    // Cases
    listCaseOptions,
    isLoadingListCaseData,
    handleSearchCase,

    // Label tickets
    listLabelTicketOptions,
    isLoadingListLabelTicketData,

    // Files
    extractedAttachments: attachments.extractedAttachments,
    uploadingFileIds: attachments.uploadingFileIds,
    isUploadingFiles: attachments.isUploadingFiles,
    handleAddFiles: attachments.handleAddFiles,
    handleRemoveFile: attachments.handleRemoveFile,

    // Loading
    isLoadingSingleTicketQuery,

    // IDs
    spaceId,
    ticketId,
  };
};

export default useDuplicateTicket;

export type TUseDuplicateTicketModalReturn = ReturnType<typeof useDuplicateTicket>;
